import { useEffect, useMemo, useState } from "react";
import { es } from "date-fns/locale";
import { toast } from "sonner";
import { Check, Loader2, CalendarCheck } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { useSite } from "@/context/SiteContext";
import { CATALOG } from "@/lib/catalog";
import { api, errorText, fmtDate, toISO } from "@/lib/api";

const STEPS = ["Servicio", "Fecha y hora", "Tus datos"];
const EMPTY = { service: "", date: "", time: "", name: "", phone: "", email: "", vehicle: "", plate: "", notes: "", whatsapp_opt_in: false };
const field = "w-full rounded-sm border border-line bg-surface px-3 py-2.5 text-sm outline-none focus:border-gold";

export const useDisabledDays = (ignoreRules) => {
  const { site } = useSite() || {};
  return useMemo(() => {
    if (!site) return () => true;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const max = new Date(today); max.setDate(max.getDate() + Number(site.booking.max_days_ahead));
    const blocked = new Set(site.blocked_dates.map((b) => b.date));
    return (d) => d < today || (!ignoreRules && (d > max || !site.hours[String((d.getDay() + 6) % 7)].enabled || blocked.has(toISO(d))));
  }, [site, ignoreRules]);
};

export const SlotPicker = ({ date, value, onChange, showAll }) => {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    if (!date) return;
    let alive = true;
    setInfo(null);
    api.get(`/availability?date=${date}`).then((r) => alive && setInfo(r.data)).catch(() => alive && setInfo({ slots: [], reason: "Error al cargar" }));
    return () => { alive = false; };
  }, [date]);
  if (!date) return <p className="text-sm text-mute">Selecciona un día en el calendario.</p>;
  if (!info) return <Loader2 className="animate-spin text-gold" />;
  if (!info.slots.length) return <p className="text-sm text-mute" data-testid="slots-empty">{info.reason || "Sin horarios"}</p>;
  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2" role="radiogroup" aria-label="Horarios disponibles">
      {info.slots.map((s) => {
        const ok = s.available || showAll;
        const sel = value === s.time;
        return (
          <button key={s.time} type="button" role="radio" aria-checked={sel} disabled={!ok} onClick={() => onChange(s.time)} data-testid={`slot-${s.time.replace(":", "")}`}
            className={`rounded-sm border px-2 py-2.5 font-mono text-sm transition-colors ${sel ? "border-gold bg-gold text-black" : ok ? "border-line hover:border-gold" : "border-surface2 text-mute2 line-through cursor-not-allowed"}`}>
            {s.time}{showAll && <span className="block text-[10px] opacity-70">{s.remaining} {s.remaining === 1 ? "libre" : "libres"}</span>}
          </button>
        );
      })}
    </div>
  );
};

const ServiceStep = ({ form, set }) => (
  <div className="grid gap-2 sm:grid-cols-2">
    {CATALOG.map((s) => {
      const sel = form.service === s.name;
      return (
        <button key={s.name} type="button" onClick={() => set({ service: s.name })} aria-pressed={sel} data-testid={`booking-service-${CATALOG.indexOf(s)}`}
          className={`flex items-center gap-3 rounded-sm border p-3 text-left text-sm transition-colors ${sel ? "border-gold bg-gold/10" : "border-line hover:border-gold/50"}`}>
          <s.icon size={18} className="shrink-0 text-gold" />{s.name}
        </button>
      );
    })}
  </div>
);

const DateStep = ({ form, set, admin }) => {
  const disabled = useDisabledDays(admin);
  const sel = form.date ? new Date(`${form.date}T12:00:00`) : undefined;
  return (
    <div className="grid gap-6 md:grid-cols-[auto,1fr]">
      <Calendar mode="single" locale={es} selected={sel} disabled={disabled} weekStartsOn={1}
        onSelect={(d) => d && set({ date: toISO(d), time: "" })} className="rounded-md border border-line bg-surface" data-testid="booking-calendar" />
      <div>
        <p className="mb-3 font-display text-lg uppercase">{form.date ? fmtDate(form.date) : "Horarios"}</p>
        <SlotPicker date={form.date} value={form.time} onChange={(time) => set({ time })} showAll={admin} />
      </div>
    </div>
  );
};

const Input = ({ id, label, ...p }) => (
  <div>
    <label htmlFor={id} className="mb-1 block text-xs uppercase tracking-wider text-mute">{label}</label>
    <input id={id} className={field} data-testid={`booking-${id}-input`} {...p} />
  </div>
);

const DataStep = ({ form, set }) => (
  <div className="grid gap-4 sm:grid-cols-2">
    <Input id="name" label="Nombre completo *" value={form.name} onChange={(e) => set({ name: e.target.value })} required autoComplete="name" />
    <Input id="phone" label="Teléfono / WhatsApp (10 dígitos) *" type="tel" value={form.phone} onChange={(e) => set({ phone: e.target.value })} required autoComplete="tel" />
    <Input id="vehicle" label="Vehículo (marca, modelo, año) *" value={form.vehicle} onChange={(e) => set({ vehicle: e.target.value })} required placeholder="Ej. Nissan Versa 2019" />
    <Input id="plate" label="Placas (opcional)" value={form.plate} onChange={(e) => set({ plate: e.target.value })} />
    <Input id="email" label="Correo (opcional)" type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} autoComplete="email" />
    <div className="sm:col-span-2">
      <label htmlFor="notes" className="mb-1 block text-xs uppercase tracking-wider text-mute">¿Qué síntomas presenta? (opcional)</label>
      <textarea id="notes" rows={3} className={field} value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="booking-notes-input" />
    </div>
    <label className="sm:col-span-2 flex items-start gap-3 rounded-sm border border-line bg-surface p-3 text-sm">
      <Checkbox checked={form.whatsapp_opt_in} onCheckedChange={(v) => set({ whatsapp_opt_in: !!v })} data-testid="booking-whatsapp-optin" className="mt-0.5 border-gold data-[state=checked]:bg-gold data-[state=checked]:text-black" />
      <span>Acepto recibir la confirmación y un recordatorio de mi cita por WhatsApp. Puedo darme de baja cuando quiera.</span>
    </label>
  </div>
);

export const BookingWizard = ({ admin = false, onDone }) => {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [override, setOverride] = useState(false);
  const set = (p) => setForm((f) => ({ ...f, ...p }));
  const canNext = [!!form.service, !!form.date && !!form.time, form.name.trim().length > 1 && form.phone.replace(/\D/g, "").length >= 10 && form.vehicle.trim().length > 1][step];

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = admin ? await api.post("/admin/appointments", { ...form, override_capacity: override }) : await api.post("/appointments", form);
      toast.success("Cita registrada");
      if (admin) { onDone?.(data); setForm(EMPTY); setStep(0); } else setDone(data);
    } catch (e) {
      toast.error(errorText(e));
    }
    setBusy(false);
  };

  if (done) return (
    <div className="rise text-center py-10" data-testid="booking-success">
      <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-green"><Check size={32} className="text-gold" /></div>
      <h3 className="mt-6 text-3xl font-bold uppercase">¡Cita registrada!</h3>
      <p className="mt-3 text-ink2">{done.service} · <span>{fmtDate(done.date)}</span> a las {done.time}</p>
      <p className="mt-2 text-sm text-mute">Te esperamos en Calle 136 #715-A, Mérida. Si necesitas cambiarla, escríbenos al 999-191-9300.</p>
      <button className="btn-ghost mt-8" onClick={() => { setDone(null); setForm(EMPTY); setStep(0); }} data-testid="booking-new-btn">Agendar otra cita</button>
    </div>
  );

  return (
    <div data-testid="appointment-booking-wizard">
      <ol className="mb-8 flex gap-2" aria-label="Pasos">
        {STEPS.map((s, i) => (
          <li key={s} className={`flex-1 border-t-4 pt-2 text-xs font-mono uppercase tracking-wider ${i <= step ? "border-gold text-green" : "border-line text-mute2"}`} aria-current={i === step ? "step" : undefined}>
            {i + 1}. {s}
          </li>
        ))}
      </ol>
      {step === 0 && <ServiceStep form={form} set={set} />}
      {step === 1 && <DateStep form={form} set={set} admin={admin} />}
      {step === 2 && <DataStep form={form} set={set} />}
      {step === 2 && admin && (
        <label className="mt-4 flex items-center gap-2 text-sm text-mute">
          <Checkbox checked={override} onCheckedChange={(v) => setOverride(!!v)} data-testid="booking-override-capacity" /> Permitir aunque el horario esté lleno o fuera de horario
        </label>
      )}
      <div className="mt-8 flex justify-between gap-3">
        <button type="button" className="btn-ghost" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="booking-back-btn">Atrás</button>
        {step < 2 ? (
          <button type="button" className="btn-gold" disabled={!canNext} onClick={() => setStep(step + 1)} data-testid="booking-next-btn">Continuar</button>
        ) : (
          <button type="button" className="btn-gold" disabled={!canNext || busy} onClick={submit} data-testid="booking-submit-btn">
            {busy ? <Loader2 size={16} className="animate-spin" /> : <CalendarCheck size={16} />} Confirmar cita
          </button>
        )}
      </div>
    </div>
  );
};
